<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>
    <template v-slot:default>
      <main>
        <section class="pb-10" v-if="filteredDokumen.length > 0">
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <h2 class="mt-8 mb-6 font-semibold text-heading-5 text-dark">
              Dokumen
            </h2>
            <div class="flex flex-col gap-2 mb-4 md:flex-row md:items-center md:justify-between">
              <div class="relative w-full md:w-1/3">
                <div class="absolute top-0 bottom-0 left-0 flex items-center pl-3 text-gray-500 pointer-events-none">
                  <MagnifyingGlassVue />
                </div>
                <input v-model="search" type="text" autocomplete="off" placeholder="Cari nama dokumen..."
                  class="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <!-- Select filter: Jenis Dokumen -->
              <div class="w-full md:w-1/3">
                <select v-model="selectedJenis"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Semua jenis dokumen</option>
                  <option v-for="opt in jenisOptions" :key="opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>
            </div>
            <div class="pt-4 border-t border-gray-3">
              <div class="flex flex-col">
                <div class="grid flex-grow grid-cols-1 gap-4 p-2 mb-6 sm:mb-8 lg:mb-10 sm:p-3 lg:p-4">
                  <div v-for="(dokumen, index) in paginatedDokumen" :key="dokumen.id || index"
                    class="flex flex-row items-start h-auto gap-4 p-5 transition-all bg-white border border-gray-200 cursor-pointer rounded-xl hover:-translate-y-1 sm:p-6 hover:shadow-md"
                    @click="viewDokumen(dokumen.iddok)">
                    <div
                      class="flex items-center justify-center w-16 h-16 overflow-hidden border border-gray-200 rounded-lg bg-gray-50 sm:mr-4 shrink-0 sm:w-20 sm:h-20">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                        class="w-8 h-8 text-blue-600 sm:w-10 sm:h-10">
                        <path fill-rule="evenodd"
                          d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z"
                          clip-rule="evenodd" />
                        <path
                          d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z" />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0 text-center sm:text-left">
                      <h3 class="mb-1 text-base font-semibold text-gray-900 sm:text-lg sm:line-clamp-2 mobile-font">
                        {{ dokumen.nama_dok }}
                      </h3>
                      <div class="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                        <span v-if="dokumen.jenisdokumen?.nama"
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          {{ dokumen.jenisdokumen.nama }}
                        </span>
                        <span v-if="dokumen.created_at" class="text-xs text-gray-500">• {{
                          formatDate(dokumen.created_at) }}</span>
                      </div>
                      <div v-if="dokumen.deskripsi" class="text-sm text-gray-600 line-clamp-2">
                        {{ (dokumen.deskripsi || "").replace(/<[^>]*>/g, "") }}
                      </div>
                    </div>
                    <div class="items-center hidden text-gray-400 sm:flex">
                      <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>

                <!-- Pagination - Always at bottom -->
                <div class="flex justify-center pt-4 mt-8 border-t border-gray-200" v-if="totalPages > 1">
                  <div class="flex flex-wrap justify-center gap-1">
                    <button v-for="p in totalPages" :key="p" @click="goToPage(p)" :class="[
                      'px-3 py-2 border border-gray-300 text-sm rounded transition-colors duration-200',
                      p === page
                        ? 'bg-red-900 text-white border-red-900'
                        : 'bg-white text-gray-700 hover:bg-red-50 hover:border-red-200 hover:text-red-900',
                    ]">
                      {{ p }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section class="pb-10" v-else>
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <h2 class="mt-8 mb-6 font-semibold text-heading-5 text-dark">
              Dokumen
            </h2>
            <div class="flex flex-col items-center justify-center py-12 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                class="w-16 h-16 mb-4 text-gray-400">
                <path fill-rule="evenodd"
                  d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625Z"
                  clip-rule="evenodd" />
                <path
                  d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z" />
              </svg>
              <h3 class="mb-2 text-lg font-semibold text-gray-900">
                Dokumen tidak ditemukan
              </h3>
              <p class="text-gray-500">
                {{
                  search
                    ? "Tidak ditemukan dokumen yang sesuai dengan pencarian."
                    : "Belum ada dokumen yang tersedia saat ini."
                }}
              </p>
            </div>
          </div>
        </section>

        <!-- Global loading overlay for fetching dokumen detail -->
        <Loading v-model:active="isLoading" :is-full-page="true" />

        <!-- Modal: View Dokumen Detail -->
        <ViewDokumen :isOpen="isModalOpen" :dokumendata="viewDokumenData" @update:isOpen="(v) => (isModalOpen = v)" />
      </main>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import ViewDokumen from "@/Components/Web/ViewDokumen.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

const props = defineProps({
  dokumen: {
    type: Array,
    required: true,
  },
});

const search = ref("");
const page = ref(1);
const perPage = 4;
const isModalOpen = ref(false);
const viewDokumenData = ref(null);
const isLoading = ref(false);
// List layout enforced; no viewMode toggle
const selectedJenis = ref("");

// Build unique, sorted list of jenis dokumen names from data
const jenisOptions = computed(() => {
  const data = Array.isArray(props.dokumen)
    ? props.dokumen
    : props.dokumen?.data || [];
  const map = new Map();
  for (const d of data) {
    const raw = d?.jenisdokumen?.nama;
    if (!raw) continue;
    const norm = raw.trim();
    const key = norm.toLowerCase();
    if (!map.has(key)) map.set(key, norm);
  }
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
});

const filteredDokumen = computed(() => {
  // Pastikan props.dokumen adalah array
  const data = Array.isArray(props.dokumen)
    ? props.dokumen
    : props.dokumen?.data || [];

  // Apply jenis filter first (if selected)
  const selected = (selectedJenis.value || "").trim().toLowerCase();
  let result = selected
    ? data.filter(
      (d) => (d?.jenisdokumen?.nama || "").trim().toLowerCase() === selected
    )
    : data;

  // Apply text search
  if (!search.value) return result;
  const q = search.value.toLowerCase();
  result = result.filter(
    (d) =>
      d.nama_dok?.toLowerCase().includes(q) ||
      (d.deskripsi && d.deskripsi.toLowerCase().includes(q)) ||
      (d.jenisdokumen?.nama && d.jenisdokumen.nama.toLowerCase().includes(q))
  );
  return result;
});

const paginatedDokumen = computed(() => {
  const start = (page.value - 1) * perPage;
  return filteredDokumen.value.slice(start, start + perPage);
});

const totalPages = computed(() =>
  Math.ceil(filteredDokumen.value.length / perPage)
);

function goToPage(p) {
  page.value = p;
}

// Reset to first page when search changes
watch(search, () => {
  page.value = 1;
});

// Reset to first page when jenis filter changes
watch(selectedJenis, () => {
  page.value = 1;
});

const viewDokumen = async (id) => {
  isLoading.value = true;
  isModalOpen.value = true;
  viewDokumenData.value = null;
  try {
    const response = await fetch(`/dokumenkinerja/${id}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Response is not JSON");
    }
    const data = await response.json();
    viewDokumenData.value = data.datadokumen;
  } catch (error) {
    console.error("Error fetching data:", error.message);
  } finally {
    isLoading.value = false;
  }
};

// Format date helper for list items
function formatDate(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
</script>

<style scoped>
/* Line clamp utilities for text truncation */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Mobile-specific font styling */
.mobile-font {
  /* Mobile font (default) */
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
  font-weight: 500;
}

/* Desktop font styling */
@media (min-width: 640px) {
  .mobile-font {
    font-family: "Roboto", "Segoe UI", -apple-system, BlinkMacSystemFont,
      sans-serif;
    font-weight: inherit;
  }
}

/* Ensure consistent hover animations */
.hover\:-translate-y-1:hover {
  transform: translateY(-0.25rem);
}
</style>
